import axios from "axios";

import { isMockEnabled } from "../lib/mock";
import { LinkedinAgent, LinkedinChange } from "../types/linkedin";

const makeEndpoint = (
  workspaceSlug: string,
  adAccountId: number,
  campaignId: number,
): string => {
  const resource = isMockEnabled() ? "mock-campaigns" : "campaigns";
  return `/api/v1/workspaces/${workspaceSlug}/integrations/linkedin/ad-accounts/${adAccountId}/${resource}/${campaignId}`;
};

export const getLinkedinAgent = async (
  workspaceSlug: string,
  adAccountId: number,
  campaignId: number,
): Promise<LinkedinAgent> => {
  return (
    await axios.get(
      `${makeEndpoint(workspaceSlug, adAccountId, campaignId)}/agent`,
    )
  ).data;
};

export interface LinkedinChangesPage {
  items: LinkedinChange[];
  meta: {
    cursor: string | null;
    total: number;
  };
}

// Bid & budget changes across an ad account's campaigns (last 30 days),
// newest first, optionally narrowed to one campaign. The cursor is
// opaque: pass back whatever the previous page returned.
export const getLinkedinChanges = async (
  workspaceSlug: string,
  adAccountId: number,
  {
    campaignId,
    cursor,
    perPage,
  }: { campaignId?: number; cursor?: string; perPage?: number } = {},
): Promise<LinkedinChangesPage> => {
  const resource = isMockEnabled() ? "mock-changes" : "changes";
  const params = new URLSearchParams();
  if (campaignId !== undefined) {
    params.set("campaign-id", String(campaignId));
  }
  if (cursor !== undefined) {
    params.set("cursor", cursor);
  }
  if (perPage !== undefined) {
    params.set("per-page", String(perPage));
  }
  return (
    await axios.get(
      `/api/v1/workspaces/${workspaceSlug}/integrations/linkedin/ad-accounts/${adAccountId}/${resource}`,
      { params },
    )
  ).data;
};

export interface LinkedinAgentStats {
  "actions-24h": number;
  "last-action-at": string | null;
}

// Workspace-wide agent activity aggregates for the dashboard's
// LinkedIn platform card.
export const getLinkedinAgentStats = async (
  workspaceSlug: string,
): Promise<LinkedinAgentStats> => {
  const resource = isMockEnabled() ? "mock-agent-stats" : "agent-stats";
  return (
    await axios.get(
      `/api/v1/workspaces/${workspaceSlug}/integrations/linkedin/${resource}`,
    )
  ).data;
};

export const startLinkedinAgent = async (
  workspaceSlug: string,
  adAccountId: number,
  campaignId: number,
): Promise<void> => {
  await axios.post(
    `${makeEndpoint(workspaceSlug, adAccountId, campaignId)}/agent`,
  );
};

export const pauseLinkedinAgent = async (
  workspaceSlug: string,
  adAccountId: number,
  campaignId: number,
): Promise<void> => {
  await axios.delete(
    `${makeEndpoint(workspaceSlug, adAccountId, campaignId)}/agent`,
  );
};
